import _ from 'lodash';
import config from 'config';
let baseUrl = config.baseUrl;
import storage from 'lib/storage';
import helpers from 'services/helpers';
import crossFetch from 'cross-fetch';
import normalize from 'normalize-object';
import { actions as userActions } from 'redux/user';
import dayjs from 'dayjs';
import { decryption, encryption } from 'lib/utils';
import { setAuthCookies, clearAuthCookies } from 'lib/cookieAuth';
const { isApiEncryptionEnabled } = config;

const inMemoryJWTManager = () => {
  let inMemoryJWT = config.REACT_APP_JWT || null;
  const requestHeaderAccessToken = config.requestHeaderAccessToken || null;
  let isRefreshing = null;

  let logoutEventName = 'ra-logout';
  // let refreshEndpoint = '/refresh-token';
  let refreshEndpoint = `${baseUrl}/BioFuelOAuthServer/Users/refresh-token`;
  let refreshTimeOutId;
  // const jwt = inMemoryJWTManager;

  const setLogoutEventName = (name) => (logoutEventName = name);
  const setRefreshTokenEndpoint = (endpoint) => (refreshEndpoint = endpoint);

  // Local lightweight UI exception logger to avoid circular deps
  const logUIException = async (ui_event = '', extra = {}) => {
    try {
      const token = getToken() || '';
      const exceptionAt = encodeURIComponent(extra?.tag || ui_event || 'UIEvent');
      const exp = (await storage.getItem('accessTokenExpiry')) || '';
      const rawStack = (() => {
        try {
          return new Error().stack || '';
        } catch (e) {
          return '';
        }
      })();
      const fileHint = (rawStack || '').split('\n')[2] || '';
      const stack = encodeURIComponent(
        `${ui_event} | ${
          typeof window !== 'undefined' ? window.location?.href || '' : ''
        } | exp=${exp} | source=${fileHint} | ${extra?.message || ''}`,
      );
      const url = `${baseUrl}/BioFuelCommonUtilities/api/CommonUtilities/InsertExceptionLog?ExceptionAt=${exceptionAt}&ExceptionStackTrace=${stack}`;
      crossFetch(url, {
        method: 'POST',
        headers: { Authorization: token },
      }).catch(() => {});
    } catch (e) {
      // ignore
    }
  };

  // This countdown feature is used to renew the JWT before it's no longer valid
  // in a way that is transparent to the user.
  const setRefreshTokenTimer = (delay) => {
    // Refresh 5 minutes before expiry instead of 60 seconds to reduce frequency
    // Ensure minimum 30 seconds to avoid too frequent refreshes
    const refreshTime = Math.max((delay - 300) * 1000, 30000);
    refreshTimeOutId = setTimeout(getRefreshedToken, refreshTime); // Validity period of the token in seconds, minus 5 minutes
  };

  const abortRefreshToken = () => {
    if (refreshTimeOutId) {
      clearTimeout(refreshTimeOutId);
    }
  };

  const waitForTokenRefresh = () => {
    if (!isRefreshing) {
      return Promise.resolve();
    }
    return isRefreshing.then(() => {
      isRefreshing = null;
      return true;
    });
  };

  const REFRESH_COOLDOWN_MS = 30000; // 30 seconds between refresh calls (cross-tab race guard)

  // The method make a call to the refresh-token endpoint
  // If there is a valid cookie, the endpoint will set a fresh jwt in memory.
  const getRefreshedToken = async () => {
    if (window.getRefreshedTokenExecuting) {
      console.log('BFC-Function getRefreshedToken is already executing, waiting for completion...');
      // Wait for the existing refresh to complete instead of aborting
      return await waitForTokenRefresh();
    }

    // Set the flag to true to indicate that the function is executing
    window.getRefreshedTokenExecuting = true;

    console.log('BFC-Function getRefreshedToken started executing...');

    let refreshToken = await storage.getItem('refreshToken');
    let pid = await storage.getItem('pid');

    if (!refreshToken) {
      ereaseToken();
      window.getRefreshedTokenExecuting = false;
      return false;
    }

    // Cross-tab pre-flight lock: keyed on the refresh token value itself.
    // Written BEFORE the API call so other tabs see it immediately and skip.
    // This closes the race where two tabs read the same refresh token and both
    // fire the API before either has written the success timestamp.
    const lockToken = await storage.getItem('refreshLockToken');
    const lockTime = parseInt((await storage.getItem('refreshLockTime')) || '0');
    const lockAge = Date.now() - lockTime;

    if (lockToken === refreshToken && lockAge < REFRESH_COOLDOWN_MS) {
      // Another tab is already refreshing (or just finished) with this exact token.
      // Wait up to 3 seconds for it to complete, then read the updated token from storage.
      console.log(
        `BFC-Function getRefreshedToken: lock held for this token (${lockAge}ms ago), waiting for other tab...`,
      );
      await new Promise((resolve) => setTimeout(resolve, Math.min(3000, REFRESH_COOLDOWN_MS - lockAge)));
      const latestToken = await storage.getItem('accessToken');
      if (latestToken) {
        inMemoryJWT = latestToken;
        window.getRefreshedTokenExecuting = false;
        return true;
      }
      // If still no token after waiting, fall through and attempt refresh anyway
    }

    // Claim the lock for this refresh token before making the API call
    await storage.setItem('refreshLockToken', refreshToken);
    await storage.setItem('refreshLockTime', String(Date.now()));

    // Post-success cooldown check (protects sequential near-duplicate calls)
    const lastRefreshTime = parseInt((await storage.getItem('lastRefreshTimestamp')) || '0');
    if (Date.now() - lastRefreshTime < REFRESH_COOLDOWN_MS) {
      const latestToken = await storage.getItem('accessToken');
      if (latestToken) {
        console.log('BFC-Function getRefreshedToken: cooldown active, syncing token from storage');
        inMemoryJWT = latestToken;
        window.getRefreshedTokenExecuting = false;
        return true;
      }
    }

    try {
      isRefreshing = doFetch(refreshEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          refresh_Token: refreshToken,
          // login_Status_Id: 0,
          // role_Id: 0,
          // person_id: parseInt(pid),
        }),
      }).ready;

      let res = await isRefreshing;

      console.log('BFC-Function getRefreshedToken api called');
      const resData = await processApiRes(res);
      //@TODO : set Autharization header in utils
      const token = _.get(resData, 'jwt_token', '');
      const tokenExpiry = _.get(resData, 'jwt_token_expire_datetime');

      refreshToken = _.get(resData, 'refresh_token', '');

      const personId = _.get(resData, 'person_id', 0);

      if (token) {
        await setToken(token, tokenExpiry);
        await setRefreshToken(refreshToken);
        await storage.setItem('lastRefreshTimestamp', String(Date.now()));

        console.log('BFC-Function getRefreshedToken completed successfully');
        // window.location.reload(false);
        return true;
      }

      console.log('BFC-Function getRefreshedToken failed - no token received');
      ereaseToken();
      return false;
    } catch (err) {
      console.log('BFC-Function getRefreshedToken error:', err);

      // NOTE: processApiErrors is intentionally NOT called here.
      // processApiErrors calls getRefreshedToken for 511 errors, which would re-enter
      // this function while window.getRefreshedTokenExecuting is still true — causing a
      // circular call that bypasses all recovery logic below.

      //avoid call to ereaseToken() in case of no internet or temporary server issues
      const message = _.get(err, 'message', '');
      const status = _.get(err, 'status', 0);

      // Only erase token for authentication-related errors, not network or server issues
      if (message === 'Network request failed' || status === 503 || status === 502 || status === 504) {
        console.log('BFC-Function getRefreshedToken failed - network/server error, keeping token');
      } else if (status === 401 || status === 403 || status === 511) {
        // Before logging out, check if a concurrent tab already refreshed with this exact token.
        // If the refreshToken in storage has changed, another tab succeeded — sync and recover.
        const currentStoredRefreshToken = await storage.getItem('refreshToken');
        if (currentStoredRefreshToken && currentStoredRefreshToken !== refreshToken) {
          const latestAccessToken = await storage.getItem('accessToken');
          if (latestAccessToken) {
            console.log(
              'BFC-Function getRefreshedToken: concurrent tab already refreshed, syncing token instead of logout',
            );
            inMemoryJWT = latestAccessToken;
            return true;
          }
        }
        console.log('BFC-Function getRefreshedToken failed - authentication error, erasing token');
        ereaseToken();
      } else {
        console.log('BFC-Function getRefreshedToken failed - unknown error, keeping token for retry');
      }
      return false;
    } finally {
      // Always reset the flag when done
      window.getRefreshedTokenExecuting = false;
    }
  };

  const getToken = () => inMemoryJWT;

  const checkTokens = async () => {
    let accessToken = await storage.getItem('accessToken');
    if (!accessToken) {
      ereaseToken();
      return false;
    }

    let accessTokenExpiry = await storage.getItem('accessTokenExpiry');

    if (!accessTokenExpiry) {
      ereaseToken();
      return false;
    }

    let refreshToken = await storage.getItem('refreshToken');

    if (!refreshToken) {
      ereaseToken();
      return false;
    }

    await setToken(accessToken, accessTokenExpiry);

    const pid = await storage.getItem('pid');
    const sid = await storage.getItem('sid');

    return {
      person_id: pid,
      social_profile_id: sid,
    };
  };

  const setToken = async (accessToken, accessTokenExpiry) => {
    inMemoryJWT = accessToken;
    globalThis.accessToken = accessToken;
    await storage.setItem('accessToken', accessToken);
    await storage.setItem('accessTokenExpiry', accessTokenExpiry);
    let accessTokenDateTime = new Date(accessTokenExpiry);
    let currentDateTime = new Date();
    let difference = parseInt((accessTokenDateTime.getTime() - currentDateTime.getTime()) / 1000);
    // const delay =  dayjs(accessTokenExpiry).diff(dayjs());
    setRefreshTokenTimer(difference);

    // Also store in cookies for cross-portal authentication
    try {
      const refreshToken = await storage.getItem('refreshToken');
      const pid = await storage.getItem('pid');
      const sid = await storage.getItem('sid');
      const loginStatusId = await storage.getItem('login_status_id');

      await setAuthCookies({
        token: accessToken,
        refreshToken: refreshToken || '',
        tokenExpiry: accessTokenExpiry || '',
        pid: pid || '',
        sid: sid || '',
        loginStatusId: loginStatusId || '',
      });
    } catch (error) {
      console.error('Error setting auth cookies:', error);
    }

    // Trigger an event to signal other tabs to refresh
    const broadcastChannel = new BroadcastChannel('authChannel');
    broadcastChannel.postMessage('loggedIn');
    broadcastChannel.close();

    return true;
  };

  const setRefreshToken = async (refreshToken) => {
    await storage.setItem('refreshToken', refreshToken);

    // Also update cookie if token is already set
    try {
      const accessToken = await storage.getItem('accessToken');
      const tokenExpiry = await storage.getItem('accessTokenExpiry');
      const pid = await storage.getItem('pid');
      const sid = await storage.getItem('sid');
      const loginStatusId = await storage.getItem('login_status_id');

      if (accessToken) {
        await setAuthCookies({
          token: accessToken,
          refreshToken: refreshToken || '',
          tokenExpiry: tokenExpiry || '',
          pid: pid || '',
          sid: sid || '',
          loginStatusId: loginStatusId || '',
        });
      }
    } catch (error) {
      console.error('Error updating auth cookies:', error);
    }

    // inMemoryJWT = token;
    // refreshToken(delay);
    return true;
  };

  const ereaseToken = async () => {
    // Log token erase event
    logUIException('erase_token', { tag: 'auth.inMemoryJWT.ereaseToken' });
    inMemoryJWT = null;

    abortRefreshToken();
    await storage.removeItem('accessToken');
    await storage.removeItem('accessTokenExpiry');
    await storage.removeItem('refreshToken');
    await storage.removeItem('persist:root');
    await storage.removeItem('pid');
    await storage.removeItem('sid');
    await storage.removeItem('posthog_uuid');
    await storage.removeItem('lastRefreshTimestamp');
    await storage.removeItem('refreshLockToken');
    await storage.removeItem('refreshLockTime');

    // Also clear cookies
    try {
      clearAuthCookies();
    } catch (error) {
      console.error('Error clearing auth cookies:', error);
    }

    // await storage.setItem(logoutEventName, Date.now());

    return true;
  };

  const ereaseAccessToken = async () => {
    // Log access token erase event
    logUIException('erase_access_token', { tag: 'auth.inMemoryJWT.ereaseAccessToken' });
    inMemoryJWT = null;

    abortRefreshToken();
    await storage.removeItem('accessToken');

    return true;
  };

  // Cross-tab access token sync: when another tab writes a new access token to localStorage,
  // update this tab's in-memory token immediately. Without this, every tab holds its stale
  // token, hits 511, refreshes independently, and cascades into repeated reloads and logout.
  // The 'storage' event fires in ALL tabs EXCEPT the one that wrote, so no ping-pong loop.
  //
  // IMPORTANT: setToken() writes 'accessToken' first, then 'accessTokenExpiry' — two separate
  // storage events. We sync inMemoryJWT on 'accessToken', and restart the timer only on
  // 'accessTokenExpiry' (where event.newValue is guaranteed to be the new expiry value).
  //
  // Timer strategy for syncing tabs: fire 30 seconds BEFORE actual expiry (not 5 min early).
  // This guarantees the tab that actively refreshed (its timer fires 5 min early) always
  // goes first. This tab is a follower — it only refreshes if the other tab is closed/absent.
  window.addEventListener('storage', (event) => {
    if (event.key === 'accessToken') {
      if (event.newValue && event.newValue !== inMemoryJWT) {
        console.log('[BFC-CROSS-TAB] Another tab refreshed — syncing access token to avoid 511');
        inMemoryJWT = event.newValue;
        globalThis.accessToken = event.newValue;
        // Timer restart happens in the accessTokenExpiry handler below (avoids reading stale expiry).
      } else if (!event.newValue) {
        console.log('[BFC-CROSS-TAB] Another tab logged out — clearing this tab token');
        inMemoryJWT = null;
        abortRefreshToken();
      }
    } else if (event.key === 'accessTokenExpiry' && event.newValue && inMemoryJWT) {
      // Use event.newValue directly — localStorage.getItem may still return the old value here.
      const remainingSecs = Math.max(0, Math.floor((new Date(event.newValue).getTime() - Date.now()) / 1000));
      // Fire 30s before actual expiry so the refreshing tab's "5 min early" timer always fires first.
      // This tab only acts as a backup refresh if the refreshing tab gets closed.
      const followerTimerMs = Math.max(remainingSecs * 1000 - 30000, 30000);
      abortRefreshToken();
      refreshTimeOutId = setTimeout(getRefreshedToken, followerTimerMs);
      console.log(
        `[BFC-CROSS-TAB] Follower timer set — fires in ${Math.round(
          followerTimerMs / 1000,
        )}s (token expires in ${remainingSecs}s)`,
      );
    }
  });

  const doFetch = (request, opts = {}, isJson = true) => {
    const controller = new AbortController();

    const { signal } = controller;

    let token = getToken() || '';

    opts = {
      method: 'GET',
      headers: {
        Authorization: token,
      },
      // credentials: 'include',
      ...opts,
    };

    if (isJson) {
      opts.headers['Content-Type'] = 'application/json';
    }

    if (
      request?.includes('BioFuelOAuthServer/Users/login') ||
      request?.includes('BioFuelOAuthServer/Users/verify-login') ||
      request?.includes('BioFuelOAuthServer/Users/refresh-token')
    ) {
      opts.headers['AccessToken'] = requestHeaderAccessToken;
    }

    const getRequest = async (req, opts) => {
      const reqString = opts?.['body'];
      let encryptedBody = encryption(reqString);
      return crossFetch(req, { ...opts, body: encryptedBody, signal });
    };
    let urlWithEncryptedQs;
    if (!opts?.['body']) {
      const parsedUrl = new URL(request);
      if (parsedUrl.search) {
        const queryParams = parsedUrl.search.split('?')[1];
        parsedUrl.search = '';
        const isAlreadyEncrypted = decryption(queryParams);
        urlWithEncryptedQs = isAlreadyEncrypted ? request : `${parsedUrl.toString()}?${queryParams}`;
      }
    }

    const req = {
      abort: () => controller.abort(),
      ready:
        isApiEncryptionEnabled === 'true'
          ? opts?.['body']
            ? getRequest(request, { ...opts, signal })
            : crossFetch(urlWithEncryptedQs || request, { ...opts, signal })
          : crossFetch(request, { ...opts, signal }),
    };

    // const req = {
    //   abort: () => controller.abort(),
    //   ready: crossFetch(request, { ...opts, signal }),
    // };

    setTimeout(() => {
      req.abort();
    }, 30 * 1000);

    return req;
  };

  const getResJSON = async (res) => {
    // debugger;
    if (!res.ok) {
      throw res;
    }

    if (res.status < 200 || res.status >= 300) {
      throw res;
    }

    let jsonRes = res;

    //incase we send response from othr than
    //fectch for processing e.g. mock response
    if (typeof res.json === 'function') {
      jsonRes = await res.json();
    }

    if (_.get(jsonRes, 'stat', '') === false) {
      throw jsonRes;
    }

    return jsonRes;
  };

  const processApiRes = async (res = {}) => {
    // const resJSON = await getResJSON(res);
    let resJSON = res;
    resJSON = isApiEncryptionEnabled === 'true' ? await res.text() : await res.json();
    if (isApiEncryptionEnabled === 'true') {
      let decryptedData = decryption(resJSON);
      resJSON = JSON.parse(decryptedData);
      if (!resJSON?.stat) {
        throw resJSON;
      }
    }

    const resData = _.get(resJSON, 'data', {}) || {};
    let snakeRes = normalize(resData, 'snake');
    return snakeRes;
  };

  const processApiErrors = async (err = {}) => {
    const errData = {
      errors: {},
    };

    const message = _.get(err, 'message', '');
    if (message === 'Network request failed') {
      errData.errors = {};
      errData.message = 'Unable to process request. please check your internet connection';
      return errData;
    }

    if (err.status === 500) {
      errData.errors = {
        500: 'Internal Server Error',
      };
      errData.message = '500 - Internal Server Error';
      return errData;
    }

    let body = {};
    try {
      body = await err.json();
      const backendFieldErrors = _.get(body, 'data') || _.get(body, 'errors') || {};
      const errMessage = _.get(body, 'message') || 'Error Processing Request';
      // errors.message = _.get(body, 'data[0]', '');

      const frontendFieldErrors = normalize(backendFieldErrors, 'snake');

      errData.errors = {
        ...frontendFieldErrors,
        // ...backendFieldErrors,
      };
      errData['message'] = errMessage || 'Unable to process request.';
    } catch (err) {
      let statusText = _.get(err, 'statusText') || '';
      const message = _.get(body, 'message') || '';
      errData['message'] = message ? message : statusText ? statusText : 'Unable to process request.';
    }

    return errData;
  };

  return {
    ereaseToken,
    getRefreshedToken,
    getToken,
    setLogoutEventName,
    setRefreshTokenEndpoint,
    setToken,
    waitForTokenRefresh,
    setRefreshToken,
    checkTokens,
    ereaseAccessToken,
  };
};

export default inMemoryJWTManager();
